// src/api/executive/dashboard-configs.api.tsx
// Per-clinic dashboard configuration (age-group buckets etc.).
import BaseAPI from 'api/api'

export interface AgeGroupBucket {
  label: string
  /** Inclusive lower bound. null = open (everyone under maxAge). */
  minAge: number | null
  /** Inclusive upper bound. null = open (everyone over minAge). */
  maxAge: number | null
}

interface AgeGroupsResponse {
  buckets: AgeGroupBucket[]
  /** True when no config row exists yet — UI may show a hint. */
  isDefault: boolean
}

export interface OccupationGroupBucket {
  label: string
  /** career.careerId values assigned to this bucket. */
  careerIds: number[]
}

interface OccupationGroupsResponse {
  buckets: OccupationGroupBucket[]
  /** False when no config exists — widget falls back to top-N raw careers. */
  isConfigured: boolean
}

export interface PromotionGroupBucket {
  label: string
  /** promotion.promotionId values that belong in this bucket. */
  promotionIds: number[]
}

interface PromotionGroupsResponse {
  buckets: PromotionGroupBucket[]
  /** False when no config exists — widget falls back to "one row per promotion". */
  isConfigured: boolean
}

/** Hard limit enforced on both client (form) and server (validator). */
export const PROMOTION_GROUPS_MAX = 10

/**
 * Procedure-type focus set — a flat list of operative-type ids the admin
 * singles out. The "รายได้เฉลี่ยต่อคนไข้" widget reads it three ways: ignore
 * (all), keep only orders touching it (include), or drop them (exclude).
 */
export interface ProcedureTypeFilterResponse {
  /** operative_types.operativeTypeId values in the admin focus set. */
  operativeTypeIds: number[]
  /** False when nothing is configured — widget keeps only its "all" mode. */
  isConfigured: boolean
}

const CONFIG_KEY_AGE_GROUPS = 'age_groups'
const CONFIG_KEY_OCCUPATION_GROUPS = 'occupation_groups'
const CONFIG_KEY_PROMOTION_GROUPS = 'promotion_groups'
const CONFIG_KEY_PROCEDURE_TYPE_FILTER = 'procedure_type_filter'

export default class DashboardConfigsApi {
  static async getAgeGroups(aboutUsId: number): Promise<AgeGroupsResponse> {
    const res = await BaseAPI.api.get(`/dashboard-configs/${CONFIG_KEY_AGE_GROUPS}`, {
      params: { aboutUsId },
    })
    return res.data
  }

  static async saveAgeGroups(aboutUsId: number, buckets: AgeGroupBucket[]): Promise<void> {
    await BaseAPI.api.put(`/dashboard-configs/${CONFIG_KEY_AGE_GROUPS}`, { aboutUsId, buckets })
  }

  static async getOccupationGroups(aboutUsId: number): Promise<OccupationGroupsResponse> {
    const res = await BaseAPI.api.get(`/dashboard-configs/${CONFIG_KEY_OCCUPATION_GROUPS}`, {
      params: { aboutUsId },
    })
    return res.data
  }

  static async saveOccupationGroups(aboutUsId: number, buckets: OccupationGroupBucket[]): Promise<void> {
    await BaseAPI.api.put(`/dashboard-configs/${CONFIG_KEY_OCCUPATION_GROUPS}`, { aboutUsId, buckets })
  }

  static async getPromotionGroups(aboutUsId: number): Promise<PromotionGroupsResponse> {
    const res = await BaseAPI.api.get(`/dashboard-configs/${CONFIG_KEY_PROMOTION_GROUPS}`, {
      params: { aboutUsId },
    })
    return res.data
  }

  static async savePromotionGroups(aboutUsId: number, buckets: PromotionGroupBucket[]): Promise<void> {
    await BaseAPI.api.put(`/dashboard-configs/${CONFIG_KEY_PROMOTION_GROUPS}`, {
      aboutUsId, buckets,
    })
  }

  static async getProcedureTypeFilter(aboutUsId: number): Promise<ProcedureTypeFilterResponse> {
    const res = await BaseAPI.api.get(`/dashboard-configs/${CONFIG_KEY_PROCEDURE_TYPE_FILTER}`, {
      params: { aboutUsId },
    })
    return res.data
  }

  static async saveProcedureTypeFilter(aboutUsId: number, operativeTypeIds: number[]): Promise<void> {
    await BaseAPI.api.put(`/dashboard-configs/${CONFIG_KEY_PROCEDURE_TYPE_FILTER}`, {
      aboutUsId, operativeTypeIds,
    })
  }
}
